import {Button, Modal, Steps} from 'antd';

import {Spinner} from '../../../../../../../../layout/spinner/spinner';
import {useCatalogInfo} from '../../../../../../../../provider/catalogs/catalogs-hook';
import {Locale} from '../../../../../../../../provider/locale/localization';
import {LangKeyType} from '../../../../../../../../provider/locale/translation/type';
import {useUser} from '../../../../../../../../provider/user/user-hook';
import {CatalogByGroupType} from '../../../../../../../../service/source-settings/catalogs/catalogs-type';

import {AppstoreAccountStepEnum, appstoreApiKeyFormId, appstoreAppsFormId} from './add-appstore-account-const';
import {AppstoreApiKeyForm} from './appstore-api-key-form/appstore-api-key-form';
import {AppstoreAppsForm} from './appstore-apps-form/appstore-apps-form';
import {AppstoreBindConfirmModal} from './appstore-bind-confirm-modal/appstore-bind-confirm-modal';
import {type UseAddAppstoreAccountFormReturnType} from './use-add-appstore-account-form';
import * as styles from './add-appstore-account.scss';

const STEPS: Array<{titleKey: LangKeyType}> = [
    {titleKey: 'SOURCE_SETTINGS__ACCOUNTS__APPSTORE__STEP1__TITLE'},
    {titleKey: 'SOURCE_SETTINGS__ACCOUNTS__APPSTORE__STEP2__TITLE'},
];

type PropsType = {
    catalog: CatalogByGroupType;
    catalogGroupId: number;
    formState: UseAddAppstoreAccountFormReturnType;
};

// eslint-disable-next-line complexity
export function AddAppstoreAccountModal(props: PropsType): JSX.Element {
    const {catalog, catalogGroupId, formState} = props;
    const {user} = useUser();
    const {getCatalogName, getCatalogLogo} = useCatalogInfo();

    const {
        accountId,
        apiKeyForm,
        appsForm,
        appsFormInitialValues,
        bindConfirmDetachedApps,
        currentStep,
        handleApiKeyFinish,
        handleAppsFinish,
        handleBindConfirmCancel,
        handleBindConfirmSave,
        handleClose,
        handleDeleteAccount,
        isBindConfirmOpen,
        isBindSaving,
        isEditMode,
        isInProgress,
        isModalOpen,
        isPrefillLoading,
        isStoreAccountAppsLoading,
        isStoreAccountLinksLoading,
        isApiKeyStepSubmittable,
        isAppsStepSubmittable,
        linkExtraAppOptions,
    } = formState;

    const isStep1 = currentStep === AppstoreAccountStepEnum.apiKey;

    function handleModalCancel(): void {
        if (isInProgress) {
            return;
        }

        handleClose();
    }

    const footer = isEditMode ? (
        <div className={styles.AddAppstoreAccount_footer}>
            <Button danger disabled={isInProgress} onClick={handleDeleteAccount}>
                <Locale stringKey="BUTTON__DELETE" />
            </Button>
            <div className={styles.AddAppstoreAccount_footerActions}>
                <Button disabled={isInProgress} onClick={handleClose}>
                    <Locale stringKey="POPUP__BUTTON__CANCEL" />
                </Button>
                <Button
                    disabled={
                        user?.isTestUser ||
                        isPrefillLoading ||
                        isStoreAccountAppsLoading ||
                        isStoreAccountLinksLoading ||
                        !isAppsStepSubmittable
                    }
                    form={appstoreAppsFormId}
                    htmlType="submit"
                    loading={isInProgress}
                    type="primary"
                >
                    <Locale stringKey="BUTTON__SAVE" />
                </Button>
            </div>
        </div>
    ) : (
        [
            <Button disabled={isInProgress} key="cancel" onClick={handleClose}>
                <Locale stringKey="POPUP__BUTTON__CANCEL" />
            </Button>,
            isStep1 ? (
                <Button
                    disabled={user?.isTestUser || !isApiKeyStepSubmittable}
                    form={appstoreApiKeyFormId}
                    htmlType="submit"
                    key="submit"
                    loading={isInProgress}
                    type="primary"
                >
                    <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__VERIFY_KEY" />
                </Button>
            ) : (
                <Button
                    disabled={
                        user?.isTestUser ||
                        isStoreAccountAppsLoading ||
                        isStoreAccountLinksLoading ||
                        !isAppsStepSubmittable
                    }
                    form={appstoreAppsFormId}
                    htmlType="submit"
                    key="submit"
                    loading={isInProgress}
                    type="primary"
                >
                    <Locale stringKey="BUTTON__ADD" />
                </Button>
            ),
        ]
    );

    return (
        <>
            <Modal
                closable={!isInProgress}
                destroyOnClose
                footer={footer}
                keyboard={!isInProgress}
                maskClosable={!isInProgress}
                onCancel={handleModalCancel}
                open={isModalOpen}
                title={
                    <Locale
                        stringKey={
                            isEditMode
                                ? 'SOURCE_SETTINGS__ACCOUNTS__EDIT_ACCOUNT__MODAL_TITLE'
                                : 'SOURCE_SETTINGS__ACCOUNTS__ADD_ACCOUNT__MODAL_TITLE'
                        }
                    />
                }
                width={520}
            >
                <p className={styles.AddAppstoreAccount_catalog}>
                    <img alt="logo" src={getCatalogLogo(catalog.id)} />
                    {getCatalogName(catalog.id)}
                </p>

                <Steps
                    className={styles.AddAppstoreAccount_steps}
                    current={currentStep}
                    items={STEPS.map(({titleKey}, index) => ({
                        title: <Locale stringKey={titleKey} />,
                        status: index < currentStep ? 'finish' : index === currentStep ? 'process' : 'wait',
                    }))}
                    size="small"
                    type="navigation"
                />

                {isPrefillLoading ? (
                    <Spinner wrapperHeight={240} />
                ) : isStep1 ? (
                    <AppstoreApiKeyForm form={apiKeyForm} formId={appstoreApiKeyFormId} onFinish={handleApiKeyFinish} />
                ) : accountId !== null ? (
                    isEditMode ? (
                        <AppstoreAppsForm
                            accountId={accountId}
                            catalogGroupId={catalogGroupId}
                            form={appsForm}
                            formId={appstoreAppsFormId}
                            initialValues={appsFormInitialValues}
                            linkExtraAppOptions={linkExtraAppOptions}
                            onFinish={handleAppsFinish}
                        />
                    ) : (
                        <AppstoreAppsForm
                            accountId={accountId}
                            catalogGroupId={catalogGroupId}
                            form={appsForm}
                            formId={appstoreAppsFormId}
                            linkExtraAppOptions={linkExtraAppOptions}
                            onFinish={handleAppsFinish}
                        />
                    )
                ) : null}
            </Modal>

            <AppstoreBindConfirmModal
                detachedApps={bindConfirmDetachedApps}
                isSaveLoading={isBindSaving}
                onCancel={handleBindConfirmCancel}
                onSave={handleBindConfirmSave}
                open={isBindConfirmOpen}
            />
        </>
    );
}
